import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Apple, AlertCircle, Lightbulb } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface NutritionalGuidanceCardProps {
  focusArea: 'pregnancy' | 'postpartum' | 'perimenopause' | 'menopause' | 'sleep' | 'nervous-system' | 'menstrual' | 'fertility';
}

const NUTRITIONAL_GUIDANCE = {
  pregnancy: {
    title: "Nutritional Support for Pregnancy",
    icon: "🤰",
    deficiencies: [
      {
        nutrient: "Folate (B9)",
        impact: "Essential for neural tube development. Deficiency increases risk of birth defects and can contribute to maternal depression and fatigue.",
        sources: "Leafy greens, legumes, fortified grains, citrus fruits"
      },
      {
        nutrient: "Iron",
        impact: "Low iron is linked to increased fatigue, brain fog, and postpartum depression risk. Supports oxygen delivery to baby and placenta.",
        sources: "Red meat, spinach, lentils, fortified cereals (pair with vitamin C for absorption)"
      },
      {
        nutrient: "Omega-3 (DHA)",
        impact: "Critical for baby's brain development and reduces maternal depression risk. DHA deficiency is associated with mood disorders.",
        sources: "Fatty fish (salmon, sardines), chia seeds, walnuts, algae supplements"
      },
      {
        nutrient: "Vitamin D",
        impact: "Low levels linked to gestational diabetes, preeclampsia, and depression. Supports immune function and mood regulation.",
        sources: "Sunlight exposure, fatty fish, fortified dairy, egg yolks, supplementation"
      },
      {
        nutrient: "Magnesium",
        impact: "Helps with leg cramps, anxiety, sleep quality, and nervous system regulation. Many pregnant women are deficient.",
        sources: "Nuts, seeds, dark chocolate, leafy greens, whole grains"
      }
    ],
    gutHealth: "A healthy gut microbiome during pregnancy supports mood, immunity, and may reduce baby's risk of allergies and eczema. Focus on fermented foods (yogurt, kefir, sauerkraut) and diverse fiber sources."
  },
  postpartum: {
    title: "Nutritional Support for Postpartum Recovery",
    icon: "👶",
    deficiencies: [
      {
        nutrient: "Iron",
        impact: "Blood loss during delivery can lead to iron depletion, causing extreme fatigue, brain fog, and increased depression risk.",
        sources: "Red meat, liver, dark leafy greens, blackstrap molasses, iron supplements if needed"
      },
      {
        nutrient: "Vitamin B12",
        impact: "Critical for energy, mood regulation, and nervous system function. Deficiency worsens postpartum depression and cognitive function.",
        sources: "Meat, fish, eggs, dairy, nutritional yeast, fortified foods"
      },
      {
        nutrient: "Zinc",
        impact: "Essential for immune function, wound healing, and neurotransmitter production. Low levels linked to postpartum depression.",
        sources: "Oysters, beef, pumpkin seeds, chickpeas, cashews"
      },
      {
        nutrient: "Omega-3 (DHA)",
        impact: "Becomes depleted during breastfeeding. Critical for mood stability and reducing postpartum depression risk.",
        sources: "Fatty fish, fish oil supplements, algae oil, chia seeds, walnuts"
      },
      {
        nutrient: "Vitamin D",
        impact: "Essential for mood, immune function, and bone health during breastfeeding. Many postpartum women are severely deficient.",
        sources: "Sunlight, fortified foods, fatty fish, supplementation (often 2000-4000 IU needed)"
      }
    ],
    gutHealth: "Postpartum gut healing is crucial: 90% of serotonin (your mood neurotransmitter) is produced in your gut. Focus on bone broth, collagen, fermented foods, and healing the gut lining with L-glutamine-rich foods (bone broth, cabbage, fermented vegetables)."
  },
  perimenopause: {
    title: "Nutritional Support for Perimenopause",
    icon: "🌸",
    deficiencies: [
      {
        nutrient: "Magnesium",
        impact: "Depletes during stress and hormonal shifts. Low levels worsen anxiety, sleep issues, hot flashes, and nervous system dysregulation.",
        sources: "Pumpkin seeds, almonds, spinach, dark chocolate, avocados"
      },
      {
        nutrient: "B Vitamins (especially B6, B12)",
        impact: "Essential for neurotransmitter production and hormone metabolism. Deficiency linked to mood swings, fatigue, and brain fog.",
        sources: "Whole grains, eggs, fish, poultry, leafy greens, nutritional yeast"
      },
      {
        nutrient: "Vitamin D",
        impact: "Critical for mood, bone health, and immune function. Low levels worsen depression and anxiety during transition.",
        sources: "Sunlight, fatty fish, fortified foods, supplementation"
      },
      {
        nutrient: "Omega-3s",
        impact: "Reduce inflammation, support brain health, and help stabilise mood during hormonal fluctuations.",
        sources: "Fatty fish, flaxseeds, chia seeds, walnuts, algae supplements"
      },
      {
        nutrient: "Phytoestrogens",
        impact: "Plant compounds that gently support declining estrogen. May help with hot flashes and mood stability.",
        sources: "Flaxseeds, soy products (tofu, tempeh, edamame), legumes, whole grains"
      }
    ],
    gutHealth: "Gut health becomes even more critical during perimenopause as estrogen decline affects gut lining integrity. Heal your gut = better serotonin production = improved mood. Focus on fermented foods, prebiotics (garlic, onions, asparagus), and anti-inflammatory foods."
  },
  menopause: {
    title: "Nutritional Support for Menopause",
    icon: "🦋",
    deficiencies: [
      {
        nutrient: "Calcium & Vitamin D",
        impact: "Post-menopausal bone loss accelerates. These nutrients are critical for bone density, mood, and overall health.",
        sources: "Dairy, fortified plant milks, sardines with bones, leafy greens, sunlight, supplements"
      },
      {
        nutrient: "Vitamin K2",
        impact: "Directs calcium to bones (not arteries). Essential for cardiovascular and bone health post-menopause.",
        sources: "Natto, hard cheeses, egg yolks, grass-fed butter, fermented foods"
      },
      {
        nutrient: "Magnesium",
        impact: "Supports sleep, reduces hot flashes, calms nervous system, and helps with mood regulation.",
        sources: "Pumpkin seeds, almonds, dark chocolate, spinach, black beans"
      },
      {
        nutrient: "B Vitamins",
        impact: "Support energy production, cognitive function, and mood during hormonal shifts.",
        sources: "Whole grains, eggs, fish, poultry, leafy greens, nutritional yeast"
      },
      {
        nutrient: "Phytoestrogens",
        impact: "May help ease menopausal symptoms by providing gentle estrogenic activity.",
        sources: "Flaxseeds (ground), soy products, chickpeas, lentils"
      }
    ],
    gutHealth: "Gut health is foundational for hormone metabolism and mood stability. A healthy gut microbiome helps metabolize and eliminate excess estrogen, reduces inflammation, and produces neurotransmitters that support emotional wellbeing."
  },
  sleep: {
    title: "Nutritional Support for Sleep",
    icon: "💤",
    deficiencies: [
      {
        nutrient: "Magnesium",
        impact: "Known as 'nature's tranquilizer.' Low levels are directly linked to insomnia, restless legs, and poor sleep quality.",
        sources: "Pumpkin seeds, almonds, spinach, dark chocolate, bananas"
      },
      {
        nutrient: "Vitamin D",
        impact: "Deficiency is strongly associated with sleep disorders, poor sleep quality, and daytime fatigue.",
        sources: "Sunlight exposure, fatty fish, egg yolks, fortified foods, supplementation"
      },
      {
        nutrient: "B6",
        impact: "Required for melatonin and serotonin production. Deficiency impairs sleep-wake cycle regulation.",
        sources: "Chickpeas, fish, poultry, potatoes, bananas, fortified cereals"
      },
      {
        nutrient: "Glycine",
        impact: "Amino acid that promotes deep sleep and lowers core body temperature for better sleep onset.",
        sources: "Bone broth, collagen, gelatin, meat on the bone"
      },
      {
        nutrient: "Tryptophan",
        impact: "Precursor to serotonin and melatonin. Essential for sleep-wake cycle regulation.",
        sources: "Turkey, chicken, eggs, cheese, nuts, seeds, oats"
      }
    ],
    gutHealth: "Your gut produces melatonin and serotonin (sleep hormones). A disrupted gut = disrupted sleep. Support gut health with fermented foods, bone broth, and reducing inflammatory foods (sugar, processed foods)."
  },
  'nervous-system': {
    title: "Nutritional Support for Nervous System",
    icon: "🧠",
    deficiencies: [
      {
        nutrient: "Magnesium",
        impact: "Essential for GABA function (calming neurotransmitter). Low levels = heightened stress response, anxiety, and nervous system dysregulation.",
        sources: "Leafy greens, nuts, seeds, dark chocolate, avocados, Epsom salt baths"
      },
      {
        nutrient: "B Vitamins (especially B1, B6, B12)",
        impact: "Critical for neurotransmitter production and nervous system function. Deficiency leads to anxiety, depression, and cognitive issues.",
        sources: "Whole grains, meat, fish, eggs, legumes, leafy greens"
      },
      {
        nutrient: "Omega-3 Fatty Acids",
        impact: "Reduce inflammation in the brain and nervous system. Support resilience to stress and emotional regulation.",
        sources: "Fatty fish, walnuts, flaxseeds, chia seeds, algae supplements"
      },
      {
        nutrient: "Vitamin C",
        impact: "Adrenal glands need vitamin C for cortisol production. Chronic stress depletes vitamin C rapidly.",
        sources: "Citrus fruits, bell peppers, strawberries, kiwi, broccoli"
      },
      {
        nutrient: "Zinc",
        impact: "Required for neurotransmitter function and stress response. Deficiency worsens anxiety and depression.",
        sources: "Oysters, beef, pumpkin seeds, chickpeas, cashews"
      }
    ],
    gutHealth: "The gut-brain axis is real: 90% of serotonin and 50% of dopamine are produced in your gut. Healing your gut = calming your nervous system. Focus on fermented foods, bone broth, reducing inflammatory foods, and supporting the vagus nerve through deep breathing and gentle movement."
  },
  menstrual: {
    title: "Nutritional Support for Menstrual Health",
    icon: "🌙",
    deficiencies: [
      {
        nutrient: "Iron",
        impact: "Lost through menstruation. Low iron causes fatigue, weakness, brain fog, and worsens PMS mood symptoms.",
        sources: "Red meat, liver, spinach, lentils, fortified cereals (with vitamin C for absorption)"
      },
      {
        nutrient: "Magnesium",
        impact: "Reduces PMS symptoms (cramps, mood swings, bloating, headaches). Most menstruating people are deficient.",
        sources: "Dark chocolate, pumpkin seeds, almonds, spinach, black beans"
      },
      {
        nutrient: "Vitamin B6",
        impact: "Helps regulate mood, reduce PMS and PMDD symptoms, and supports hormone balance.",
        sources: "Chickpeas, fish, poultry, bananas, potatoes, fortified cereals"
      },
      {
        nutrient: "Calcium",
        impact: "Reduces PMS symptoms including mood swings, fatigue, and food cravings by 50% in studies.",
        sources: "Dairy, fortified plant milks, leafy greens, sardines, almonds"
      },
      {
        nutrient: "Omega-3s",
        impact: "Reduce menstrual cramps, inflammation, and mood-related PMS symptoms.",
        sources: "Fatty fish, flaxseeds, chia seeds, walnuts, algae supplements"
      }
    ],
    gutHealth: "Hormone metabolism happens in your gut. Poor gut health = poor hormone clearance = worse PMS. Support estrogen metabolism with cruciferous vegetables (broccoli, cauliflower, Brussels sprouts), fiber, and fermented foods. Heal gut = balance hormones = better cycle experience."
  },
  fertility: {
    title: "Nutritional Support for Fertility & IVF",
    icon: "💝",
    deficiencies: [
      {
        nutrient: "Folate (B9)",
        impact: "Critical for egg quality, DNA synthesis, and preventing neural tube defects. Low levels reduce IVF success rates and increase miscarriage risk.",
        sources: "Leafy greens, legumes, asparagus, avocado, fortified grains"
      },
      {
        nutrient: "Vitamin D",
        impact: "Low levels are linked to reduced fertility, lower IVF success rates, PCOS, and pregnancy complications. Essential for egg development and implantation.",
        sources: "Sunlight, fatty fish, egg yolks, fortified foods, supplementation (often 2000-4000 IU needed)"
      },
      {
        nutrient: "CoQ10",
        impact: "Improves egg quality, especially important for women over 35. Supports mitochondrial function in eggs and may improve IVF outcomes.",
        sources: "Organ meats, fatty fish, spinach, broccoli, supplementation (ubiquinol form)"
      },
      {
        nutrient: "Omega-3 (DHA/EPA)",
        impact: "Reduces inflammation, improves egg quality, supports embryo development, and may improve IVF success rates.",
        sources: "Fatty fish (salmon, sardines), fish oil supplements, algae oil, walnuts"
      },
      {
        nutrient: "Selenium",
        impact: "Powerful antioxidant that protects eggs from oxidative damage. Deficiency linked to miscarriage and reduced fertility.",
        sources: "Brazil nuts (2-3 daily), seafood, eggs, sunflower seeds"
      },
      {
        nutrient: "Zinc",
        impact: "Essential for hormone balance, egg development, and implantation. Deficiency impairs fertility in both partners.",
        sources: "Oysters, beef, pumpkin seeds, chickpeas, cashews"
      }
    ],
    gutHealth: "Gut health directly impacts fertility through hormone metabolism and inflammation. A healthy gut microbiome supports estrogen balance, reduces inflammatory markers that affect implantation, and improves nutrient absorption critical for egg quality. Focus on fermented foods, diverse fiber, bone broth, and reducing inflammatory foods (sugar, processed foods, excess alcohol)."
  }
};

export function NutritionalGuidanceCard({ focusArea }: NutritionalGuidanceCardProps) {
  const guidance = NUTRITIONAL_GUIDANCE[focusArea];

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/10 border-primary/20">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{guidance.icon}</div>
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Apple className="h-5 w-5 text-primary" />
              {guidance.title}
            </h3>
            <p className="text-sm text-muted-foreground">Research-backed nutritional insights</p>
          </div>
        </div>

        <Alert className="bg-primary/5 border-primary/20">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            <strong>Important:</strong> This information is for educational purposes only and is not medical advice. 
            Always consult with your healthcare provider before making significant dietary changes or starting supplements, 
            especially during pregnancy, postpartum, or if you have health conditions.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Common Deficiencies & Their Impact</h4>
          </div>
          
          <div className="space-y-4">
            {guidance.deficiencies.map((item, index) => (
              <div key={index} className="bg-background/80 p-4 rounded-lg border">
                <div className="flex items-start gap-2 mb-2">
                  <Badge variant="secondary" className="mt-0.5">{item.nutrient}</Badge>
                </div>
                <p className="text-sm mb-3 font-medium">{item.impact}</p>
                <div className="bg-secondary/10 p-3 rounded border border-secondary/20">
                  <p className="text-xs font-semibold text-primary mb-1">Food Sources:</p>
                  <p className="text-sm">{item.sources}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <span>🦠</span>
            The Gut-Brain Connection
          </h4>
          <p className="text-sm leading-relaxed">{guidance.gutHealth}</p>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            <strong>Why this matters:</strong> Addressing nutritional deficiencies can significantly improve mood, 
            energy, hormone balance, and overall wellbeing. Many symptoms attributed to "just hormones" or stress 
            may actually be rooted in nutritional gaps that are relatively easy to address.
          </p>
          <p className="mt-2">
            Consider working with a registered dietitian or functional medicine practitioner for personalised guidance.
          </p>
        </div>
      </div>
    </Card>
  );
}